import React, {useEffect, useRef, useState, useMemo, useCallback} from "react";
import InputField from "../../components/comman/input.js";
import Textarea from "../../components/comman/textarea.js";
import PhoneInput from "../../components/comman/phoneInput.js";
import AddressInput from "../../components/comman/addressInput.js";
import Button from "../../components/comman/button.js";
import {updateChef, uploadChefBanner, deleteChefBanner} from "../../services/profile-service.js";
import Toast from "../../components/Toast/toast.js";
import {useAuth} from "../../utils/authContext.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import PictureUploader from "../pictureUploader.js";

const ProfileOverview = ({chefData}) => {
    const toastRef = useRef(null);
    const { chefInfo, refreshChefData } = useAuth();
    const { language } = useLanguage();
    const [isEditMode, setIsEditMode] = useState(false);
    const [formData, setFormData] = useState({});
    const [originalFormData, setOriginalFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    
    // Memoize avatar and banner URLs to prevent unnecessary rerenders
    // Use stable string comparison to avoid re-renders when object reference changes but URL doesn't
    const avatarUrl = useMemo(() => {
        const url = chefData?.avatarUrl || null;
        return url;
    }, [chefData?.avatarUrl]); // Only recalculate if the actual URL string changes
    
    const bannerUrl = useMemo(() => {
        const url = chefData?.bannerUrl || null;
        return url;
    }, [chefData?.bannerUrl]); // Only recalculate if the actual URL string changes

    // Memoize validation error objects to prevent unnecessary re-renders of PictureUploader
    const avatarValidationError = useMemo(() => {
        return errors.avatarUrl ? { url: errors.avatarUrl } : null;
    }, [errors.avatarUrl]);

    const bannerValidationError = useMemo(() => {
        return errors.bannerUrl ? { url: errors.bannerUrl } : null;
    }, [errors.bannerUrl]);

    // Memoize setValidationError callbacks to prevent unnecessary re-renders of PictureUploader
    const setAvatarValidationError = useCallback((callback) => {
        setErrors(prev => {
            const newErrors = typeof callback === 'function' ? callback(prev) : callback;
            // Extract url error if present
            if (newErrors && typeof newErrors === 'object' && 'url' in newErrors) {
                return { ...prev, avatarUrl: newErrors.url || null };
            }
            return { ...prev };
        });
    }, []); // Empty deps - function doesn't depend on any changing values

    const setBannerValidationError = useCallback((callback) => {
        setErrors(prev => {
            const newErrors = typeof callback === 'function' ? callback(prev) : callback;
            // Extract url error if present
            if (newErrors && typeof newErrors === 'object' && 'url' in newErrors) {
                return { ...prev, bannerUrl: newErrors.url || null };
            }
            return { ...prev };
        });
    }, []); // Empty deps - function doesn't depend on any changing values

    useEffect(() => {
        try {
            if (!chefData || typeof chefData !== 'object') {
                return;
            }
            
            if (chefData) {
            const socialLinks = {};
            if (chefData.socialLinkDtos && chefData.socialLinkDtos.length > 0) {
                chefData.socialLinkDtos.forEach(link => {
                    if (link.socialType) {
                        socialLinks[`socialLink_${link.socialType.toLowerCase()}`] = link.link || "";
                    }
                });
            }
            // Ensure phone number has +374 prefix
            let phoneNumber = chefData.phoneNumber || '';
            if (phoneNumber && !phoneNumber.startsWith('+374')) {
                // If phone number doesn't start with +374, add it
                phoneNumber = '+374' + phoneNumber.replace(/^\+374/, '');
            }
            
            // Get address from chefLegalDto.address (backend format) and map to frontend format
            // Backend uses: city -> frontend uses: residence
            // Backend uses: intercom -> frontend uses: domofon
            const chefLegalDtoAddress = chefData.chefLegalDto?.address;
            let mappedChefLegalDtoAddress = null;
            if (chefLegalDtoAddress && typeof chefLegalDtoAddress === 'object') {
                mappedChefLegalDtoAddress = {
                    country: chefLegalDtoAddress.country || "",
                    region: chefLegalDtoAddress.region || "",
                    residence: chefLegalDtoAddress.residence || chefLegalDtoAddress.city || "", // Map city to residence
                    street: chefLegalDtoAddress.street || "",
                    home: chefLegalDtoAddress.home || "",
                    floor: chefLegalDtoAddress.floor || "",
                    entrance: chefLegalDtoAddress.entrance || "",
                    flat: chefLegalDtoAddress.flat || "",
                    domofon: chefLegalDtoAddress.domofon || chefLegalDtoAddress.intercom || "", // Map intercom to domofon
                    // Backend returns coordinates as [lng, lat], so swap when extracting
                    latitude: chefLegalDtoAddress.latitude || (chefLegalDtoAddress.coordinates && Array.isArray(chefLegalDtoAddress.coordinates) ? chefLegalDtoAddress.coordinates[1] : null),
                    longitude: chefLegalDtoAddress.longitude || (chefLegalDtoAddress.coordinates && Array.isArray(chefLegalDtoAddress.coordinates) ? chefLegalDtoAddress.coordinates[0] : null),
                    // Convert backend [lng, lat] format to frontend [lat, lng] format
                    coordinates: chefLegalDtoAddress.coordinates && Array.isArray(chefLegalDtoAddress.coordinates) && chefLegalDtoAddress.coordinates.length >= 2
                        ? [chefLegalDtoAddress.coordinates[1], chefLegalDtoAddress.coordinates[0]] // Swap to [lat, lng]
                        : (chefLegalDtoAddress.latitude && chefLegalDtoAddress.longitude ? [chefLegalDtoAddress.latitude, chefLegalDtoAddress.longitude] : null),
                };
            }

            // Normalize coordinates - handle both array [lat, lng] and object {lat, lng} formats
            // Note: Backend returns coordinates as [lng, lat], but frontend uses [lat, lng]
            let normalizedCoordinates = null;
            const addressDtoCoords = chefData.addressDto?.coordinates;
            const addressCoords = chefData.address?.coordinates;
            const chefLegalDtoCoords = mappedChefLegalDtoAddress?.coordinates;
            
            if (addressDtoCoords != null) {
                if (Array.isArray(addressDtoCoords) && addressDtoCoords.length >= 2) {
                    // Backend format is [lng, lat], convert to [lat, lng] for frontend
                    normalizedCoordinates = [addressDtoCoords[1], addressDtoCoords[0]];
                } else if (addressDtoCoords && typeof addressDtoCoords === 'object' && !Array.isArray(addressDtoCoords)) {
                    if (addressDtoCoords.lat !== undefined && addressDtoCoords.lng !== undefined) {
                        normalizedCoordinates = [addressDtoCoords.lat, addressDtoCoords.lng];
                    } else if (addressDtoCoords.latitude !== undefined && addressDtoCoords.longitude !== undefined) {
                        normalizedCoordinates = [addressDtoCoords.latitude, addressDtoCoords.longitude];
                    }
                }
            } else if (addressCoords != null) {
                if (Array.isArray(addressCoords) && addressCoords.length >= 2) {
                    // Backend format is [lng, lat], convert to [lat, lng] for frontend
                    normalizedCoordinates = [addressCoords[1], addressCoords[0]];
                } else if (addressCoords && typeof addressCoords === 'object' && !Array.isArray(addressCoords)) {
                    if (addressCoords.lat !== undefined && addressCoords.lng !== undefined) {
                        normalizedCoordinates = [addressCoords.lat, addressCoords.lng];
                    } else if (addressCoords.latitude !== undefined && addressCoords.longitude !== undefined) {
                        normalizedCoordinates = [addressCoords.latitude, addressCoords.longitude];
                    }
                }
            } else if (chefLegalDtoCoords != null) {
                if (Array.isArray(chefLegalDtoCoords) && chefLegalDtoCoords.length >= 2) {
                    // Backend format is [lng, lat], convert to [lat, lng] for frontend
                    normalizedCoordinates = [chefLegalDtoCoords[1], chefLegalDtoCoords[0]];
                } else if (chefLegalDtoCoords && typeof chefLegalDtoCoords === 'object' && !Array.isArray(chefLegalDtoCoords)) {
                    if (chefLegalDtoCoords.lat !== undefined && chefLegalDtoCoords.lng !== undefined) {
                        normalizedCoordinates = [chefLegalDtoCoords.lat, chefLegalDtoCoords.lng];
                    } else if (chefLegalDtoCoords.latitude !== undefined && chefLegalDtoCoords.longitude !== undefined) {
                        normalizedCoordinates = [chefLegalDtoCoords.latitude, chefLegalDtoCoords.longitude];
                    }
                }
            } else if (chefData.addressDto?.latitude != null && chefData.addressDto?.longitude != null) {
                normalizedCoordinates = [chefData.addressDto.latitude, chefData.addressDto.longitude];
            } else if (chefData.address?.latitude != null && chefData.address?.longitude != null) {
                normalizedCoordinates = [chefData.address.latitude, chefData.address.longitude];
            } else if (mappedChefLegalDtoAddress?.latitude != null && mappedChefLegalDtoAddress?.longitude != null) {
                normalizedCoordinates = [mappedChefLegalDtoAddress.latitude, mappedChefLegalDtoAddress.longitude];
            }

            // Safely get address data with proper fallbacks
            const addressDto = (chefData.addressDto && typeof chefData.addressDto === 'object') ? chefData.addressDto : null;
            const address = (chefData.address && typeof chefData.address === 'object') ? chefData.address : null;
            
            // Safely extract latitude and longitude with chefLegalDto.address as fallback
            const safeLatitude = (addressDto && typeof addressDto.latitude !== 'undefined' && addressDto.latitude !== null) 
                ? addressDto.latitude 
                : ((address && typeof address.latitude !== 'undefined' && address.latitude !== null) 
                    ? address.latitude 
                    : ((mappedChefLegalDtoAddress && typeof mappedChefLegalDtoAddress.latitude !== 'undefined' && mappedChefLegalDtoAddress.latitude !== null)
                        ? mappedChefLegalDtoAddress.latitude
                        : null));
            const safeLongitude = (addressDto && typeof addressDto.longitude !== 'undefined' && addressDto.longitude !== null) 
                ? addressDto.longitude 
                : ((address && typeof address.longitude !== 'undefined' && address.longitude !== null) 
                    ? address.longitude 
                    : ((mappedChefLegalDtoAddress && typeof mappedChefLegalDtoAddress.longitude !== 'undefined' && mappedChefLegalDtoAddress.longitude !== null)
                        ? mappedChefLegalDtoAddress.longitude
                        : null));
            
            // Parse telegramId from comma-separated string to array
            let telegramIds = [];
            if (chefData.telegramId) {
                telegramIds = chefData.telegramId
                    .split(',')
                    .map(id => id.trim())
                    .filter(id => id.length > 0);
            }
            // Ensure at least one empty field if no Telegram IDs exist
            if (telegramIds.length === 0) {
                telegramIds = [''];
            }

            const initialData = {
                ...chefData,
                ...socialLinks,
                avatarUrl: avatarUrl || "",
                bannerUrl: bannerUrl || "",
                phoneNumber: phoneNumber,
                username: chefData.username || "",
                link: chefData.username || "", // Map username to link for UI
                address: addressDto || address || mappedChefLegalDtoAddress || {
                    country: "Armenia",
                    region: "",
                    residence: "",
                    street: "",
                    home: "",
                    floor: "",
                    entrance: "",
                    flat: "",
                    domofon: "",
                    latitude: safeLatitude,
                    longitude: safeLongitude,
                    coordinates: normalizedCoordinates,
                },
                // Map kitchen fields - assuming API uses kitchenHy, kitchenRu, kitchenEn or kitchenAm, kitchenRu, kitchenEn
                kitchenHy: chefData.kitchenHy || chefData.kitchenAm || '',
                kitchenRu: chefData.kitchenRu || '',
                kitchenEn: chefData.kitchenEn || '',
                // Map description fields
                descriptionHy: chefData.descriptionHy || chefData.descriptionAm || '',
                descriptionRu: chefData.descriptionRu || '',
                descriptionEn: chefData.descriptionEn || '',
                // Telegram IDs array
                telegramIds: telegramIds,
            };
            
            // Only update formData if something actually changed (prevent unnecessary rerenders)
            // Skip avatar/banner URL comparison since they're memoized separately
            // CRITICAL: Don't reset telegramIds if we're in edit mode - user might be editing them
            const currentTelegramIds = JSON.stringify(telegramIds);
            const existingTelegramIds = JSON.stringify(formData.telegramIds || []);
            
            const hasChanges = Object.keys(formData).length === 0 || 
                formData.phoneNumber !== phoneNumber ||
                formData.username !== (chefData.username || "") ||
                formData.link !== (chefData.username || "") ||
                JSON.stringify(formData.address) !== JSON.stringify(addressDto || address || mappedChefLegalDtoAddress) ||
                formData.kitchenHy !== (chefData.kitchenHy || chefData.kitchenAm || '') ||
                formData.descriptionHy !== (chefData.descriptionHy || chefData.descriptionAm || '') ||
                currentTelegramIds !== existingTelegramIds;
            
            if (hasChanges || Object.keys(formData).length === 0) {
                const dataToSet = {...initialData, file: formData.file || null, bannerFile: formData.bannerFile || null};
                setFormData(dataToSet);
                // Only update originalFormData if not in edit mode (to preserve user's edits)
                if (!isEditMode) {
                    setOriginalFormData({...initialData, file: null, bannerFile: null});
                }
            }
            if (chefData.fullNameHy) {
                chefInfo(chefData.fullNameHy);
            }
        }
        } catch (error) {
            console.error("Error processing chefData in ProfileOverview:", error);
            // Convert any non-Error objects to proper Error instances
            if (!(error instanceof Error)) {
                const errorMessage = error?.message || error?.toString() || "Unknown error occurred";
                throw new Error(`ProfileOverview data processing error: ${errorMessage}`);
            }
            throw error;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chefData, chefInfo, isEditMode]); // Removed avatarUrl and bannerUrl from deps - they're derived from chefData, so chefData dependency is sufficient


    const validateField = (name, value) => {
        let error = "";
        
        if (name === "email") {
            if (!value) {
                error = getTranslation('validation.required', language);
            } else if (!/\S+@\S+\.\S+/.test(value)) {
                error = getTranslation('validation.invalidEmail', language);
            }
        } else if (name === "phoneNumber") {
            if (!value) {
                error = getTranslation('validation.required', language);
            } else {
                // Remove prefix to check number part
                const numberPart = value.replace(/^\+374/, '');
                if (numberPart.length !== 8) {
                    error = getTranslation('validation.phoneNumberLength', language) || 'Phone number must be 8 digits';
                } else if (!/^\d{8}$/.test(numberPart)) {
                    error = getTranslation('validation.phoneNumberInvalid', language) || 'Phone number must contain only digits';
                }
            }
        } else if (name === "fullNameHy") {
            if (!value) {
                error = getTranslation('validation.required', language);
            }
        } else if (name === "descriptionHy") {
            if (!value) {
                error = getTranslation('validation.required', language);
            }
        } else if (name === "kitchenHy") {
            if (!value) {
                error = getTranslation('validation.required', language);
            }
        } else if (name === "avatarUrl") {
            if (!value) {
                error = getTranslation('validation.required', language);
            }
        } else if (name === "link") {
            // Link validation - required when privateDomain is true
            if (chefData?.privateDomain === true) {
                if (!value || value.trim() === "") {
                    error = getTranslation('profile.linkRequired', language) || getTranslation('validation.required', language);
                } else if (value.length < 2) {
                    error = getTranslation('validation.minLength', language) || 'Must be at least 2 characters';
                } else if (!/^[a-z0-9-]+$/.test(value)) {
                    error = getTranslation('validation.invalidFormat', language) || 'Only lowercase letters, numbers, and hyphens are allowed';
                } else if (value.includes(" ")) {
                    error = getTranslation('validation.noSpaces', language) || 'Spaces are not allowed';
                }
            }
        }
        
        return error;
    };

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["email", "phoneNumber", "fullNameHy", "descriptionHy", "kitchenHy", "avatarUrl"];
        
        // Add link to required fields if privateDomain is true
        if (chefData?.privateDomain === true) {
            requiredFields.push("link");
        }
        
        requiredFields.forEach(field => {
            const error = validateField(field, formData[field]);
            if (error) {
                newErrors[field] = error;
            }
        });
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLinkChange = (e) => {
        const { value } = e.target;
        // Normalize to lowercase and only allow lowercase letters, numbers, and hyphens (DNS-compliant for subdomains)
        const normalizedValue = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
        
        setFormData((prevData) => ({
            ...prevData,
            link: normalizedValue,
        }));
        
        setTouched(prev => ({
            ...prev,
            link: true
        }));
        
        const error = validateField('link', normalizedValue);
        setErrors(prev => ({
            ...prev,
            link: error
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
        
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));
        
        const error = validateField(name, value);
        setErrors(prev => ({
            ...prev,
            [name]: error
        }));
    };

    const handleBlur = (name) => {
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));
        
        const error = validateField(name, formData[name]);
        setErrors(prev => ({
            ...prev,
            [name]: error
        }));
    };

    const handleLogoImageChange = useCallback((file) => {
        if (file) {
            // Create preview URL for display
            const previewUrl = URL.createObjectURL(file);
                setFormData((prevData) => ({
                    ...prevData,
                avatarUrl: previewUrl,
                    file,
                }));
                // Clear avatar error if file is selected
                setErrors(prev => ({
                    ...prev,
                    avatarUrl: null
                }));
        }
    }, []); // No dependencies - uses functional setState

    const handleLogoImageRemove = useCallback(() => {
        // Allow deletion, but validation will prevent saving without logo
        setFormData((prevData) => ({
            ...prevData,
            avatarUrl: null,
            file: null,
        }));
        
        // Mark as touched to show error
        setTouched(prev => ({
            ...prev,
            avatarUrl: true
        }));
        setErrors(prev => ({
            ...prev,
            avatarUrl: getTranslation('validation.required', language)
        }));
    }, [language]); // Only depends on language

    const handleBannerImageChange = useCallback((file) => {
        if (file) {
            // Revoke previous preview URL to prevent memory leak
            setFormData((prevData) => {
                if (prevData.bannerUrl && prevData.bannerUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(prevData.bannerUrl);
                }
                // Create preview URL for display
                const previewUrl = URL.createObjectURL(file);
                return {
                    ...prevData,
                    bannerUrl: previewUrl,
                    bannerFile: file,
                };
            });
            // Clear banner error if file is selected
            setErrors(prev => ({
                ...prev,
                bannerUrl: null
            }));
        }
    }, []); // No dependencies - uses functional setState

    const handleBannerImageRemove = useCallback(() => {
        // Revoke preview URL if it's a blob URL
        setFormData((prevData) => {
            if (prevData.bannerUrl && prevData.bannerUrl.startsWith('blob:')) {
                URL.revokeObjectURL(prevData.bannerUrl);
            }
            return {
                ...prevData,
                bannerUrl: null,
                bannerFile: null,
            };
        });
    }, []); // No dependencies - uses functional setState

    const handleTelegramIdChange = (index, value) => {
        // Only allow numeric characters (0-9)
        let numericValue = value.replace(/[^0-9]/g, '');
        
        // Limit to 25 digits to prevent abuse
        if (numericValue.length > 25) {
            numericValue = numericValue.substring(0, 25);
        }
        
        setFormData((prevData) => {
            const newTelegramIds = [...(prevData.telegramIds || [])];
            newTelegramIds[index] = numericValue;
            return {
                ...prevData,
                telegramIds: newTelegramIds,
            };
        });
    };

    const handleAddTelegramId = () => {
        setFormData((prevData) => {
            const currentIds = prevData.telegramIds || [];
            const newIds = [...currentIds, ''];
            return {
                ...prevData,
                telegramIds: newIds,
            };
        });
    };

    const handleRemoveTelegramId = (index) => {
        setFormData((prevData) => {
            const currentTelegramIds = prevData.telegramIds || [''];
            
            // Validate index
            if (index < 0 || index >= currentTelegramIds.length) {
                return prevData;
            }
            
            // Prevent removing the last Telegram ID
            if (currentTelegramIds.length <= 1) {
                toastRef.current?.addToast(
                    getTranslation('profile.lastTelegramIdCannotBeRemoved', language),
                    "error"
                );
                return prevData; // Return unchanged state
            }
            
            const newTelegramIds = [...currentTelegramIds];
            newTelegramIds.splice(index, 1);
            // Ensure at least one empty field remains
            if (newTelegramIds.length === 0) {
                newTelegramIds.push('');
            }
            return {
                ...prevData,
                telegramIds: newTelegramIds,
            };
        });
    };


    const handleEditClick = () => {
        setIsEditMode(true);
        setTouched({});
        setErrors({});
        // Ensure telegramIds is initialized when entering edit mode
        setFormData((prevData) => {
            if (!prevData.telegramIds || prevData.telegramIds.length === 0) {
                return {
                    ...prevData,
                    telegramIds: [''],
                };
            }
            return prevData;
        });
    };

    const handleCancelClick = () => {
        // Revoke blob URLs to prevent memory leaks
        if (formData.bannerUrl && formData.bannerUrl.startsWith('blob:')) {
            URL.revokeObjectURL(formData.bannerUrl);
        }
        if (formData.avatarUrl && formData.avatarUrl.startsWith('blob:')) {
            URL.revokeObjectURL(formData.avatarUrl);
        }
        
        setIsEditMode(false);
        setFormData({...originalFormData, file: null, bannerFile: null});
        setTouched({});
        setErrors({});
        const fileInput = document.getElementById("fileInput");
        if (fileInput) {
            fileInput.value = "";
        }
    };

    const handleUpdateClick = async () => {
        if (!validateForm()) {
            const allTouched = {};
            Object.keys(formData).forEach(key => {
                allTouched[key] = true;
            });
            setTouched(allTouched);
            toastRef.current?.addToast(getTranslation('validation.fixErrors', language), "error");
            return;
        }
        
        setIsSaving(true);
        // Build updatedData according to backend ChefUpdateDto schema
        // Only include fields that exist in the schema
        const updatedData = {
            // Optional: chefLabelDtos
            chefLabelDtos: formData.chefLabelDtos || [],
            // Optional: socialLinks (note: backend expects "socialLinks", not "socialLinkDtoList")
            socialLinkDtoList: [
                {
                    id: chefData?.socialLinkDtos?.find(link => link.socialType === "TIKTOK")?.id || null,
                    link: formData.socialLink_tiktok || '',
                    socialType: "TIKTOK",
                },
                {
                    id: chefData?.socialLinkDtos?.find(link => link.socialType === "YOUTUBE")?.id || null,
                    link: formData.socialLink_youtube || '',
                    socialType: "YOUTUBE",
                },
                {
                    id: chefData?.socialLinkDtos?.find(link => link.socialType === "FACEBOOK")?.id || null,
                    link: formData.socialLink_facebook || '',
                    socialType: "FACEBOOK",
                },
                {
                    id: chefData?.socialLinkDtos?.find(link => link.socialType === "INSTAGRAM")?.id || null,
                    link: formData.socialLink_instagram || '',
                    socialType: "INSTAGRAM",
                },
            ],
            // Optional: email
            email: formData.email,
            // Optional: phoneNumber
            phoneNumber: formData.phoneNumber,
            // Optional: address (will be sent as chefLegalDto.address in updateChef service)
            address: formData.address,
            // Optional: kitchenAm (only Am version, not Ru/En - backend doesn't accept kitchenRu/kitchenEn)
            kitchenAm: formData.kitchenHy,
            // Optional: descriptionAm (only Am version, not Ru/En - backend doesn't accept descriptionRu/descriptionEn)
            descriptionAm: formData.descriptionHy,
            // Optional: fullNameAm
            fullNameAm: formData.fullNameHy,
            // Optional: telegramId (array will be converted to comma-separated string in profile-service)
            telegramId: formData.telegramIds || []
        };

        // Add username if privateDomain is true (always send it so backend can validate it's required)
        if (chefData?.privateDomain === true) {
            updatedData.username = formData.link || "";
        }

        try {
            const file = formData.file;
            // Validate file if provided
            if (file && (!(file instanceof File) || file.size === 0)) {
                toastRef.current?.addToast(
                    getTranslation('profile.invalidFile', language) || "Invalid file. Please select a valid image file.",
                    "error"
                );
                setIsSaving(false);
                return;
            }
            
            // Handle banner: upload new or delete existing
            if (formData.bannerFile) {
                // Upload new banner
                // Revoke preview URL before upload
                if (formData.bannerUrl && formData.bannerUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(formData.bannerUrl);
                }
                await uploadChefBanner(chefData.id, formData.bannerFile);
            } else if (!formData.bannerUrl && chefData?.bannerUrl) {
                // Banner was removed - delete from server
                await deleteChefBanner(chefData.id);
            }
            
            await updateChef(updatedData, chefData.id, file);
            
            // Refresh chef data to get the updated avatar and banner URLs from the server
            await refreshChefData();
            
            // Update formData with the new URLs from refreshed chefData
            setFormData((prevData) => ({
                ...prevData,
                file: null,
                bannerFile: null,
                avatarUrl: chefData?.avatarUrl || prevData.avatarUrl,
                bannerUrl: chefData?.bannerUrl || prevData.bannerUrl
            }));
            
            toastRef.current?.addToast(getTranslation('profile.updateSuccess', language), "success");
            setOriginalFormData({...formData, file: null, bannerFile: null});
            setIsEditMode(false);
        } catch (error) {
            // Extract more detailed error message from API response
            let errorMessage = error.response?.data?.message || 
                              error.response?.data?.error || 
                              error.message || 
                              getTranslation('profile.updateError', language) || "Failed to update profile";
            
            // Handle specific error codes with user-friendly messages
            if (error.response?.data?.code === 100006 || 
                errorMessage === 'invalid-file-type' || 
                errorMessage?.toLowerCase().includes('invalid-file-type')) {
                const fileExtension = formData.file?.name?.split('.').pop()?.toUpperCase() || 'unknown';
                let invalidFileMessage = getTranslation('menu.invalidFileTypeWithExtension', language);
                if (invalidFileMessage && invalidFileMessage.includes('{extension}')) {
                    invalidFileMessage = invalidFileMessage.replace('{extension}', fileExtension);
                } else {
                    invalidFileMessage = getTranslation('menu.invalidFileType', language) || 
                        `Invalid file type (${fileExtension}). Please provide an image file (JPG, PNG, JPEG, WEBP, HEIC, HEIF).`;
                }
                errorMessage = invalidFileMessage;
                // Also set the error in form state
                setErrors(prev => ({
                    ...prev,
                    avatarUrl: errorMessage
                }));
            }
            
            toastRef.current?.addToast(errorMessage, "error");
            console.error("Error during update:", error.response?.data || error.message || error);
        } finally {
            setIsSaving(false);
        }
    };

    const isFormChanged = () => {
        if (!originalFormData || !formData) return false;
        if (formData.file !== originalFormData.file) return true;
        if (formData.bannerFile !== originalFormData.bannerFile) return true;
        if (originalFormData.avatarUrl && !formData.avatarUrl) return true;
        if (originalFormData.bannerUrl && !formData.bannerUrl) return true;

        const fieldsToCompare = [
            'fullNameHy', 'fullNameEn', 'fullNameRu',
            'email', 'phoneNumber', 'address',
            'kitchenHy', 'kitchenEn', 'kitchenRu',
            'descriptionHy', 'descriptionEn', 'descriptionRu',
            'socialLink_tiktok', 'socialLink_youtube', 'socialLink_facebook', 'socialLink_instagram',
            'link'
        ];

        for (const field of fieldsToCompare) {
            if (formData[field] !== originalFormData[field]) {
                return true;
            }
        }
        
        // Compare telegramIds arrays
        const telegramIdsChanged = JSON.stringify(formData.telegramIds || []) !== JSON.stringify(originalFormData.telegramIds || []);
        if (telegramIdsChanged) {
            return true;
        }
        
        return false;
    };

    return (
        <div className="mt-6">
            <Toast ref={toastRef}/>

            {/* Image Modal */}
            {/* Edit Button */}
            {!isEditMode && (
                <div className="flex justify-end items-center mb-6">
                    <Button
                        variant="secondary"
                        size="md"
                        onClick={handleEditClick}
                        className="flex items-center"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        {getTranslation('common.edit', language)}
                    </Button>
                </div>
            )}

            {/* Logo Section - At the top */}
            <div className="bg-white dark:bg-[#161b22] rounded-lg border border-gray-200 dark:border-[#30363d] p-6 mb-6 shadow-sm">
                <label className="block text-base font-semibold text-gray-900 dark:text-[#c9d1d9] mb-4">
                    {getTranslation('profile.logo', language)}
                    {isEditMode && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
                </label>
                <div className="max-w-md">
                    <PictureUploader
                        dishImage={avatarUrl}
                        onImageRemoved={handleLogoImageRemove}
                        setImage={handleLogoImageChange}
                        image={formData.file || null}
                        validationError={avatarValidationError}
                        setValidationError={setAvatarValidationError}
                        isEditable={isEditMode}
                    />
                </div>
            </div>

            {/* Banner Section */}
            <div className="bg-white dark:bg-[#161b22] rounded-lg border border-gray-200 dark:border-[#30363d] p-6 mb-6 shadow-sm">
                <label className="block text-base font-semibold text-gray-900 dark:text-[#c9d1d9] mb-4">
                    {getTranslation('profile.banner', language) || 'Banner'}
                </label>
                <div className="max-w-2xl">
                    <PictureUploader
                        dishImage={bannerUrl}
                        onImageRemoved={handleBannerImageRemove}
                        setImage={handleBannerImageChange}
                        image={formData.bannerFile || null}
                        validationError={bannerValidationError}
                        setValidationError={setBannerValidationError}
                        isEditable={isEditMode}
                    />
                </div>
            </div>

            {/* Basic Information Card */}
            <div className="bg-white dark:bg-[#161b22] rounded-lg border border-gray-200 dark:border-[#30363d] p-6 mb-6 shadow-sm">
                <h3 className="text-base font-semibold text-gray-900 dark:text-[#c9d1d9] mb-4">
                    {getTranslation('profile.basicInformation', language) || 'Basic Information'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                <InputField
                    value={formData.fullNameHy || ''}
                    onChange={handleChange}
                    onBlur={() => handleBlur('fullNameHy')}
                    name="fullNameHy"
                    label={getTranslation('profile.fullNameAm', language)}
                    labelFor="name-am"
                    placeholder={getTranslation('profile.fullNameAmPlaceholder', language)}
                    isRequired={true}
                    showRequiredIndicator={isEditMode}
                    error={touched.fullNameHy && errors.fullNameHy}
                            disabled={!isEditMode}
                />
                    </div>
                    <div>
                <InputField
                            value={formData.email || ''}
                    onChange={handleChange}
                    onBlur={() => handleBlur('email')}
                    name="email"
                    label={getTranslation('profile.email', language)}
                    labelFor="email"
                    inputType="email"
                    placeholder={getTranslation('profile.emailPlaceholder', language)}
                    isRequired={true}
                    showRequiredIndicator={isEditMode}
                    error={touched.email && errors.email}
                            disabled={!isEditMode}
                />
            </div>
                    {chefData?.privateDomain === true && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-[#b1bac4] mb-2">
                                {getTranslation('profile.link', language) || 'Link'}
                                {isEditMode && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
                            </label>
                            <div className="flex items-center">
                                <input
                                    id="link"
                                    type="text"
                                    value={formData.link || ''}
                                    onChange={handleLinkChange}
                                    onBlur={() => handleBlur('link')}
                                    placeholder={getTranslation('profile.linkPlaceholder', language) || 'yourbusiness'}
                                    disabled={!isEditMode}
                                    className={`w-full px-3 py-2 border rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                                        touched.link && errors.link
                                            ? 'border-red-500 dark:border-red-400'
                                            : 'border-gray-300 dark:border-[#30363d]'
                                    } ${
                                        !isEditMode
                                            ? 'bg-gray-100 dark:bg-[#0d1117] text-gray-500 dark:text-[#8b949e] cursor-not-allowed'
                                            : 'bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9]'
                                    }`}
                                />
                                <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 dark:border-[#30363d] bg-gray-50 dark:bg-[#161b22] text-gray-500 dark:text-[#8b949e] text-sm">
                                    .bychef.am
                                </span>
                            </div>
                            {touched.link && errors.link && (
                                <p className="text-red-500 dark:text-red-400 text-xs mt-1">{errors.link}</p>
                            )}
                        </div>
                    )}
                    <div>
                        <PhoneInput
                            value={formData.phoneNumber || ''}
                    onChange={handleChange}
                    onBlur={() => handleBlur('phoneNumber')}
                    name="phoneNumber"
                    label={getTranslation('profile.phoneNumber', language)}
                    labelFor="phone"
                            placeholder="XX XXX XXX"
                    isRequired={true}
                    showRequiredIndicator={isEditMode}
                    error={touched.phoneNumber && errors.phoneNumber}
                            disabled={!isEditMode}
                />
            </div>
                    <div className="md:col-span-2">
                        <AddressInput
                            value={formData.address || {}}
                            onChange={handleChange}
                            name="address"
                            label={getTranslation('profile.address', language)}
                            labelFor="address"
                            isRequired={false}
                            showRequiredIndicator={isEditMode}
                            error={touched.address && errors.address}
                            disabled={!isEditMode}
                        />
                    </div>
                </div>

                {/* Page Name Fields (formerly Kitchen Name) */}
                <div className="mt-6">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-[#b1bac4] mb-4">
                        {getTranslation('profile.pageName', language)}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <InputField
                                value={formData.kitchenHy || ''}
                                onChange={handleChange}
                                onBlur={() => handleBlur('kitchenHy')}
                                name="kitchenHy"
                                label={getTranslation('profile.pageNameAm', language)}
                                labelFor="page-name-am"
                                placeholder={getTranslation('profile.pageNameAmPlaceholder', language)}
                                isRequired={true}
                                showRequiredIndicator={isEditMode}
                                error={touched.kitchenHy && errors.kitchenHy}
                                disabled={!isEditMode}
                            />
                        </div>
                        <div>
                            <InputField
                                value={formData.kitchenRu || ''}
                                onChange={handleChange}
                                onBlur={() => handleBlur('kitchenRu')}
                                name="kitchenRu"
                                label={getTranslation('profile.pageNameRu', language)}
                                labelFor="page-name-ru"
                                placeholder={getTranslation('profile.pageNameRuPlaceholder', language)}
                                error={touched.kitchenRu && errors.kitchenRu}
                                disabled={!isEditMode}
                            />
                        </div>
                        <div>
                            <InputField
                                value={formData.kitchenEn || ''}
                                onChange={handleChange}
                                onBlur={() => handleBlur('kitchenEn')}
                                name="kitchenEn"
                                label={getTranslation('profile.pageNameEn', language)}
                                labelFor="page-name-en"
                                placeholder={getTranslation('profile.pageNameEnPlaceholder', language)}
                                error={touched.kitchenEn && errors.kitchenEn}
                                disabled={!isEditMode}
                            />
                    </div>
                </div>
            </div>

                {/* Description Fields */}
                <div className="mt-6">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-[#b1bac4] mb-4">
                        {getTranslation('profile.description', language)}
                    </h4>
                    <div className="space-y-4">
                        <div>
                <Textarea
                                value={formData.descriptionHy || ''}
                    onChange={handleChange}
                    onBlur={() => handleBlur('descriptionHy')}
                    name="descriptionHy"
                    label={getTranslation('profile.descriptionAm', language)}
                    labelFor="description-am"
                    placeholder={getTranslation('profile.descriptionAmPlaceholder', language)}
                    isRequired={true}
                    showRequiredIndicator={isEditMode}
                    error={touched.descriptionHy && errors.descriptionHy}
                                disabled={!isEditMode}
                />
            </div>
            <div>
                            <Textarea
                                value={formData.descriptionRu || ''}
                                onChange={handleChange}
                                onBlur={() => handleBlur('descriptionRu')}
                                name="descriptionRu"
                                label={getTranslation('profile.descriptionRu', language)}
                                labelFor="description-ru"
                                placeholder={getTranslation('profile.descriptionRuPlaceholder', language)}
                                error={touched.descriptionRu && errors.descriptionRu}
                                disabled={!isEditMode}
                            />
                        </div>
                <div>
                            <Textarea
                                value={formData.descriptionEn || ''}
                                onChange={handleChange}
                                onBlur={() => handleBlur('descriptionEn')}
                                name="descriptionEn"
                                label={getTranslation('profile.descriptionEn', language)}
                                labelFor="description-en"
                                placeholder={getTranslation('profile.descriptionEnPlaceholder', language)}
                                error={touched.descriptionEn && errors.descriptionEn}
                                disabled={!isEditMode}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Telegram IDs Card */}
            <div className="bg-white dark:bg-[#161b22] rounded-lg border border-gray-200 dark:border-[#30363d] p-6 mb-6 shadow-sm">
                <h3 className="text-base font-semibold text-gray-900 dark:text-[#c9d1d9] mb-4">
                    {getTranslation('profile.telegramIds', language)}
                </h3>
                
                {((formData.telegramIds && formData.telegramIds.length > 0) ? formData.telegramIds : ['']).map((id, index) => {
                    const telegramIdsArray = formData.telegramIds || [''];
                    const isLastId = telegramIdsArray.length === 1;
                    return (
                        <div key={`telegram-id-${index}`} className="flex items-start gap-2 mb-4">
                            <div className="flex-1">
                                <InputField
                                    value={id || ''}
                                    onChange={(e) => handleTelegramIdChange(index, e.target.value)}
                                    name={`telegramId_${index}`}
                                    label={`${getTranslation('profile.telegramId', language)} ${index + 1}`}
                                    labelFor={`telegram-id-${index}`}
                                    placeholder={getTranslation('profile.telegramIdPlaceholder', language)}
                                    inputType="tel"
                                    maxLength={25}
                                    disabled={!isEditMode}
                                />
                            </div>
                            {isEditMode && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleRemoveTelegramId(index);
                                    }}
                                    disabled={isLastId}
                                    className={`mt-8 p-2 text-red-500 hover:text-red-700 transition-colors relative z-10 ${
                                        isLastId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                    }`}
                                    style={{ pointerEvents: isLastId ? 'none' : 'auto' }}
                                    title={isLastId ? getTranslation('profile.lastTelegramIdCannotBeRemoved', language) : getTranslation('profile.removeTelegramId', language)}
                                    aria-label={getTranslation('profile.removeTelegramId', language)}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    );
                })}
                
                {isEditMode && (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleAddTelegramId();
                        }}
                        type="button"
                        className="mt-2"
                    >
                        {getTranslation('profile.addTelegramId', language)}
                    </Button>
                )}
            </div>

            {/* Social Links Card */}
            <div className="bg-white dark:bg-[#161b22] rounded-lg border border-gray-200 dark:border-[#30363d] p-6 mb-6 pb-8 shadow-sm">
                <h3 className="text-base font-semibold text-gray-900 dark:text-[#c9d1d9] mb-6">
                    {getTranslation('profile.socialLinksAndBranding', language)}
                </h3>

                {/* Social Links Section */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-[#b1bac4] mb-4">
                        {getTranslation('profile.socialLinks', language)}
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {['TikTok', 'YouTube', 'Facebook', 'Instagram'].map((socialType) => {
                                const fieldName = `socialLink_${socialType.toLowerCase()}`;
                                return (
                                    <InputField
                                        key={socialType}
                                        isSocialLink={true}
                                        value={formData[fieldName] || ''}
                                        onChange={(e) => {
                                            const {value} = e.target;
                                            setFormData((prevData) => ({
                                                ...prevData,
                                                [fieldName]: value,
                                            }));
                                        }}
                                        name={fieldName}
                                        label={socialType}
                                        labelFor={socialType.toLowerCase()}
                                        inputType="url"
                                        placeholder={getTranslation('profile.enterSocialUrl', language).replace('{platform}', socialType)}
                                        isLabelLeft={true}
                                    disabled={!isEditMode}
                                    />
                                );
                            })}
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            {isEditMode && (
                <div className="flex items-center justify-end space-x-3 pt-4 pb-6 sm:pb-8 border-t border-gray-200 dark:border-[#30363d]">
                    <Button
                        variant="secondary"
                        size="md"
                        onClick={handleCancelClick}
                    >
                        {getTranslation('common.cancel', language)}
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                    onClick={handleUpdateClick}
                        disabled={isSaving || !isFormChanged()}
                        loading={isSaving}
                        className="flex items-center"
                    >
                        {!isSaving && (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        )}
                        {isSaving ? getTranslation('common.saving', language) : getTranslation('common.save', language)}
                    </Button>
                </div>
                )}
        </div>
    );
};

// Memoize component to prevent re-renders when props haven't changed
export default React.memo(ProfileOverview, (prevProps, nextProps) => {
    // Only re-render if chefData reference changed AND avatar/banner URLs actually changed
    const prevAvatarUrl = prevProps.chefData?.avatarUrl;
    const nextAvatarUrl = nextProps.chefData?.avatarUrl;
    const prevBannerUrl = prevProps.chefData?.bannerUrl;
    const nextBannerUrl = nextProps.chefData?.bannerUrl;
    
    // Return true if props are equal (don't re-render), false if different (re-render)
    // Only re-render if avatar or banner URLs actually changed
    return prevAvatarUrl === nextAvatarUrl && prevBannerUrl === nextBannerUrl;
});
